/**
 * #111887: tracks whether the inventories for the currently requested product
 * list have loaded, correlating each inventory request with the product list
 * it belongs to so a stale settlement (the success or failure of a superseded
 * request) can neither reopen the gate nor overwrite the inventory map.
 *
 * `loaded` is the reactive flag the catalog binds to `inventories-loaded`;
 * the internal token is what invalidates out-of-order callbacks.
 */
function createInventoryGate() {
    let current = 0;
    return {
        loaded: false,

        // A new product list was requested: mark any in-flight inventory
        // request as stale. The returned ticket lets the product request's own
        // callbacks ask whether they still represent the current list — a stale
        // products failure must not restart (and thereby invalidate) the
        // current list's inventory sync.
        //
        // This deliberately does NOT hide the badges: the old list stays on
        // screen (with its still-correct stock) until the new one arrives, so
        // hiding here made every badge vanish on click and pop back later.
        // The caller hides via hide() right before it swaps the list in.
        close() {
            current++;
            const ticket = current;
            return {
                isCurrent: () => ticket === current,
            };
        },

        // The new product list is about to be rendered and its stock is not
        // known yet — hide the badges until refreshInventories() settles.
        // Ticket-neutral: it does not supersede any request.
        hide() {
            this.loaded = false;
        },

        // A new inventory request is starting for the current product list.
        // The returned ticket settles the gate only if no newer product list
        // or inventory request has superseded it in the meantime.
        begin() {
            current++;
            const ticket = current;
            return {
                // Returns true if this request still owns the gate — the
                // caller must skip its side effects (map writes) when false.
                settle: () => {
                    if (ticket !== current) {
                        return false;
                    }
                    this.loaded = true;
                    return true;
                },
            };
        },

        // Nothing to fetch (no products, or no inventories URL) — open the
        // gate immediately so badges are not hidden forever.
        release() {
            current++;
            this.loaded = true;
        },
    };
}

module.exports = { createInventoryGate };
